/**
 * The floating feedback button, plus the panel it opens.
 *
 * Mounted once in the authenticated shell rather than per view: the pages that get a button are
 * decided by `resolveFeedbackSurface`, so adding a Talent Acquisition page needs no edit here and
 * cannot ship without its button. Renders nothing at all on every other page.
 */
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquarePlus } from 'lucide-react';
import { describeSurface, resolveFeedbackSurface, type FeedbackSurface } from './feedbackSurfaces';
import FeedbackPanel from './FeedbackPanel';

export default function FeedbackWidget() {
  const location = useLocation();
  /**
   * The surface is FROZEN when the panel opens, not re-read from the route while it is open.
   *
   * The route can change underneath an open report — the TA board's 15-minute idle reload
   * canonicalises its own URL, and the sidebar is still clickable behind the drawer. Reading the
   * live route would silently re-file a half-written report against a different page, or, if the
   * new route has no button, unmount the panel and throw the draft away mid-sentence.
   */
  const [openSurface, setOpenSurface] = useState<FeedbackSurface | null>(null);
  const surface = resolveFeedbackSurface(location.pathname);

  if (!surface && !openSurface) return null;

  return (
    <>
      {surface && (
        <button
          type="button"
          onClick={() => setOpenSurface(surface)}
          title={`Send feedback about ${describeSurface(surface)}`}
          aria-label={`Send feedback about ${describeSurface(surface)}`}
          className="fixed bottom-5 right-5 z-40 flex items-center gap-1.5 rounded-full bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-lg transition-colors hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
        >
          <MessageSquarePlus className="h-4 w-4" />
          Feedback
        </button>
      )}

      {openSurface && (
        <FeedbackPanel
          surface={openSurface}
          open
          onOpenChange={(next) => {
            if (!next) setOpenSurface(null);
          }}
        />
      )}
    </>
  );
}
